import { decodeHttpEncodedQueryString } from 'wcs-integration/decodeHttpEncodedQueryString';
import { loadStubs } from 'stubs';
import { dataLayer } from 'analytics/data-layer';
import { addToTrolleyListener } from 'redux/listeners/add-to-trolley';
import { loginListener } from 'redux/listeners/login';
import setSentryContextListener from 'redux/listeners/set-sentry-context-listener';

import { getIsPreloaded } from 'redux/modules/page/selectors';
import { isSignedOnAsCustomer } from 'redux/modules/sessions/selectors';

import {
  rehydrateServerGtmEvents,
  rehydrateServerMonetateEvents,
} from 'redux/modules/page/actions';

import { generateCustomerDataLayerEvent } from 'redux/modules/login/customer-datalayer-event';
import { saveLastNonAuthenticationPath } from 'redux/modules/login/actions/save-last-non-authentication-path';
import { clearError } from 'redux/modules/sessions/actions/clear-error';
import { sessionDetailsLoaded as sessionDetailsLoadedAction } from 'redux/modules/sessions/actions/session-details-loaded';
import { refreshSession } from 'redux/modules/sessions/refresh-session';
import { checkResetPasswordExpiryDate } from 'route-actions/check-reset-password-expiry-date';
import { findRouteMatch } from 'route-data/find-route-match';
import { oneTimeFetch } from 'route-data/one-time-fetch';
import history from 'utils/history';
import { PAGE_DATA_PRELOAD_IN_PROGRESS } from 'redux/modules/page/actions/types';
import { clientPageLoaded } from 'redux/modules/page/actions/client-page-loaded';
import { yieldToMain } from 'utils/yield-to-main';

const preRenderActions = async (store, client) => {
  const { dispatch, getState } = store;

  decodeHttpEncodedQueryString();
  checkResetPasswordExpiryDate();

  dispatch(saveLastNonAuthenticationPath());

  await loadStubs(getState());

  addToTrolleyListener(store);
  loginListener(store);
  setSentryContextListener(store);

  dispatch(rehydrateServerGtmEvents());
  dispatch(rehydrateServerMonetateEvents());

  let dataFetchComplete;
  if (!getIsPreloaded(getState())) {
    const { sessionGenerated } = refreshSession(dispatch, getState, {
      dataLoad: false,
    });

    // Preload any JS for fetchers while app is relatively idle waiting for session generation.
    // The preload function is not designed to be awaited on so the initial app render can start
    // ASAP should they not load in time. However local testing at time of writing finds it often loads
    // in time.
    //
    // Should loading of the fetcher modules happen in time, data fetchers should be initiated before
    // first render making optimum use of network resources during the long task of first render.
    if (client) {
      const routeMatch = findRouteMatch(history.getLocation().pathname);
      if (routeMatch && routeMatch.fetch) {
        routeMatch.preload();
      }
    }
    await sessionGenerated;
    if (client) {
      // Start initial api data load
      dataFetchComplete = (async () => {
        dispatch({ type: PAGE_DATA_PRELOAD_IN_PROGRESS });
        await oneTimeFetch(
          { path: history.getLocation().pathname, url: undefined },
          { dispatch },
          client,
        );
        dispatch(clientPageLoaded());
      })();
      // yieldToMain forces the data fetching to start before initial rendering. Additionally, where the fetchers
      // are in the same module as the React component, the JS and CSS for the page also load earlier and are
      // then included in the first render rather than an update.
      await yieldToMain();
    }
  }

  const customerDataLayerEventPromise = (async () => {
    await dataFetchComplete;

    dispatch(clearError());

    const dataLayerEventName = isSignedOnAsCustomer(getState()) ? 'sign_on_as_customer' : null;

    dispatch(sessionDetailsLoadedAction());
    return generateCustomerDataLayerEvent(getState(), dataLayerEventName);
  })();

  // The data layer abstraction understands the promise and waits for it to resolve giving the option
  // to allow the rest of the function to continue.
  dataLayer.push(customerDataLayerEventPromise);
};

export default preRenderActions;
